import React from 'react';

/**
 * Swipe a bubble to the right to reply — WhatsApp's touch gesture.
 *
 * Pointer events, touch only: a mouse drag would fight text selection. A
 * vertical movement bigger than the horizontal one hands the gesture back to
 * the scroller (`touch-action: pan-y` on the row keeps scrolling native), so
 * scrolling a thread never triggers a reply by accident. The row follows the
 * finger up to a cap and springs back on release; past the threshold, it
 * replies.
 */
const CAP = 64;
const THRESHOLD = 48;

export default function useSwipeToReply(onReply, { enabled = true } = {}) {
	const [dx, setDx] = React.useState(0);
	const start = React.useRef(null);

	const onPointerDown = (e) => {
		if (!enabled || e.pointerType !== 'touch') return;
		start.current = { x: e.clientX, y: e.clientY, cancelled: false };
	};

	const onPointerMove = (e) => {
		const s = start.current;
		if (!s || s.cancelled) return;
		const mx = e.clientX - s.x;
		const my = e.clientY - s.y;
		if (Math.abs(my) > Math.abs(mx) && Math.abs(my) > 8) {
			s.cancelled = true;
			setDx(0);
			return;
		}
		if (mx > 0) setDx(Math.min(CAP, mx));
	};

	const end = () => {
		const s = start.current;
		start.current = null;
		if (s && !s.cancelled && dx >= THRESHOLD) onReply?.();
		setDx(0);
	};

	return {
		dx,
		handlers: { onPointerDown, onPointerMove, onPointerUp: end, onPointerCancel: end },
	};
}
